<ng-container *transloco="let t; prefix: 'group-management.panels.participant-import'">
  <app-panel-body>
    <div class="flex min-h-0 flex-1 flex-col text-sm text-primary-text">
      <div class="min-h-0 flex-1 overflow-y-auto">
        <app-content-container>
          <div class="space-y-4">
            <p>{{ t('format-intro') }} {{ t('format-header') }}</p>

            <input
              id="participant-import-file"
              type="file"
              accept=".csv,text/csv"
              class="sr-only"
              [disabled]="submitting()"
              (change)="onFileSelected($event)"
            />
            <label
              for="participant-import-file"
              data-testid="participant-import-drop-zone"
              class="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors hover:border-teal hover:bg-teal/5"
              [class.border-teal]="dragging()"
              [class.bg-teal/5]="dragging()"
              [class.border-primary-border]="!dragging()"
              (dragover)="onDragOver($event)"
              (dragleave)="dragging.set(false)"
              (drop)="onDrop($event)"
            >
              <span class="text-3xl text-teal" aria-hidden="true">⬆</span>
              @if (fileName(); as name) {
                <span class="font-semibold">{{ name }}</span>
                <span class="text-teal underline">{{ t('choose-different-file') }}</span>
              } @else {
                <span class="font-semibold">{{ t('drop-file') }}</span>
                <span class="text-teal underline">{{ t('browse-file') }}</span>
              }
            </label>

            @if (fileName() === null) {
              <p class="text-secondary-text">
                {{ t('template-prompt') }}
                <a class="text-teal underline" [href]="templateUrl" download="participant-import-template.csv">{{
                  t('download-template')
                }}</a>
              </p>
            }

            @if (partialFailure(); as failure) {
              <app-inline-alert
                severity="danger"
                data-testid="participant-import-partial-failure"
                [message]="t('partial-failure', { landed: failure.landed, sent: failure.sent })"
              />
            }

            @if (fileName() !== null && tooManyRows()) {
              <app-inline-alert
                severity="danger"
                data-testid="participant-import-too-many-rows"
                [message]="t('too-many-rows', { max: maxRows })"
              />
            } @else if (fileName() !== null) {
              <p class="font-semibold" data-testid="participant-import-summary">
                {{ t('summary', { invite: inviteRows().length, skipped: skippedRows().length }) }}
              </p>

              @if (rosterSizeChange(); as change) {
                <app-inline-alert
                  severity="warning"
                  data-testid="participant-import-roster-size-warning"
                  [message]="t('roster-size-warning', { from: change.from, to: change.to })"
                />
              }

              @if (inviteRows().length > 0) {
                <ul class="divide-y divide-primary-border rounded-md border border-primary-border">
                  @for (row of inviteRows(); track $index) {
                    <li class="flex items-start gap-3 px-3 py-3">
                      <input
                        type="checkbox"
                        class="mt-1"
                        [checked]="isChecked(row)"
                        [disabled]="submitting()"
                        (change)="toggle(row)"
                        [attr.aria-label]="t('select-person', { name: row.firstname + ' ' + row.lastname })"
                      />
                      <div class="min-w-0 flex-1">
                        <p class="font-semibold">{{ row.firstname }} {{ row.lastname }}</p>
                        <p class="break-all text-secondary-text">{{ row.email }}</p>
                      </div>
                      <span class="shrink-0 rounded-full bg-teal/10 px-2 py-0.5 text-xs font-semibold text-primary-text">
                        <span class="sr-only">{{ t('status-prefix') }} </span>{{ statusLabel(row) }}
                      </span>
                    </li>
                  }
                </ul>
              }

              @if (skippedRows().length > 0) {
                <details class="rounded-md border border-primary-border">
                  <summary class="cursor-pointer px-3 py-3 font-semibold">
                    {{ t('skipped-heading', { count: skippedRows().length }) }}
                  </summary>
                  <ul class="divide-y divide-primary-border border-t border-primary-border">
                    @for (row of skippedRows(); track $index) {
                      <li class="flex items-start gap-3 px-3 py-3">
                        <input
                          type="checkbox"
                          class="mt-1"
                          disabled
                          [attr.aria-label]="t('select-person', { name: row.firstname + ' ' + row.lastname })"
                        />
                        <div class="min-w-0 flex-1">
                          <p class="font-semibold">{{ row.firstname }} {{ row.lastname }}</p>
                          <p class="break-all text-secondary-text">{{ row.email }}</p>
                        </div>
                        <span class="shrink-0 rounded-full bg-danger/10 px-2 py-0.5 text-xs font-semibold text-primary-text">
                          <span class="sr-only">{{ t('status-prefix') }} </span>{{ statusLabel(row) }}
                        </span>
                      </li>
                    }
                  </ul>
                </details>
              }
            }
          </div>
        </app-content-container>
      </div>

      <div class="px-4 pt-3" role="status" aria-live="polite">
        @if (submitting()) {
          <p class="text-secondary-text" data-testid="participant-import-sending-note">
            <span class="sr-only">{{ sendingLabel() }}. </span>{{ t('sending-note') }}
          </p>
        }
      </div>

      <div class="flex shrink-0 flex-col gap-3 border-t border-primary-border px-4 py-4">
        <app-button
          variant="primary"
          [fullWidth]="true"
          [loading]="submitting()"
          [disabled]="checkedCount() === 0"
          (clicked)="submit()"
        >
          {{ submitting() ? sendingLabel() : inviteButtonLabel() }}
        </app-button>
        <app-button variant="outline" [fullWidth]="true" [disabled]="submitting()" (clicked)="cancel()">
          {{ t('close') }}
        </app-button>
      </div>
    </div>
  </app-panel-body>
</ng-container>
